import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type UseInfiniteQueryResult,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { z } from "zod";

import { withJWTClient } from "../connectClient";
import { mediaLibraryListResponseSchema } from "./mediaLibrarySchema";
import { resolveMockCatalogAssets } from "./mocks/mockCatalogAssets";
import {
  resolveMockAllMappingFieldValues,
  resolveMockCharacteristics,
  resolveMockMappingFieldValues,
  resolveMockUpdateCharacteristics,
  resolveMockUpdateMappingFieldValues,
} from "./mocks/mockCharacteristics";
import {
  resolveMockFieldMappingConfig,
  resolveMockUpdateFieldMappingConfig,
} from "./mocks/mockFieldMappingConfig";
import {
  resolveMockIngestedProduct,
  resolveMockIngestedVariant,
} from "./mocks/mockIngestedProduct";
import { resolveMockMappingFields } from "./mocks/mockMappingFields";
import { resolveMockMediaLibrary } from "./mocks/mockMediaLibrary";
import { resolveMockProductReviews } from "./mocks/mockProductReviews";
import {
  resolveMockProductCards,
  resolveMockSampleProducts,
  resolveMockSearchBrands,
  resolveMockSearchCatalog,
  resolveMockSearchCategories,
  resolveMockSearchCollections,
  resolveMockSearchProducts,
  resolveMockSearchTags,
} from "./mocks/mockSearchProducts";
import type {
  GetCatalogAssetsParams,
  GetCatalogAssetsResponse,
  MediaLibraryListParams,
  MediaLibraryListResponse,
} from "@/types/catalogAssets";
import type {
  AllMappingFieldValuesResponse,
  CharacteristicsResponse,
  FieldMappingConfigResponse,
  MappingFieldValuesResponse,
  MappingFieldsResponse,
  UpdateCharacteristicsRequest,
  UpdateMappingFieldValuesRequest,
  UpdateFieldMappingConfigRequest,
} from "@/types/fieldMappingConfig";
import type {
  CatalogHandleSearchResponse,
  CatalogSearchResponse,
  GetProductAssociationsParams,
  GetProductCardsResponse,
  GetProductEngagementParams,
  IngestedProductSchema,
  IngestedVariantSchema,
  ListCategoriesResponse,
  ListCollectionsResponse,
  ListProductCardsParams,
  ListProductCardsResponse,
  ListProductGroupsParams,
  ListTagsResponse,
  PatchProductOverrideRequest,
  ProductCardSortBy,
  ProductCardsTaxonomyFilter,
  ProductEngagementResponse,
  ResetProductOverrideRequest,
  SearchProductsParams,
  SearchProductsResponse,
  SkuSearchRow,
} from "@/types/ingestedProduct";
import type {
  GetProductReviewsParams,
  GetProductReviewsResponse,
} from "@/types/productReviews";

import { getBackendErrorMessage, isNotFoundError } from "@/utils/backendError";
import { isMock } from "@/utils/mockConfig";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

const USE_CATALOG_MOCKS_ENV = import.meta.env.VITE_USE_CATALOG_MOCKS === "true";
const shouldMockCatalog = () => isMock("products") || USE_CATALOG_MOCKS_ENV;

const API_BASE_URL: string =
  (import.meta.env.VITE_CATALOG_API_URL as string) || "";

// Python's server default; Go's differs, so always send it explicitly.
const CATALOG_ASSETS_DEFAULT_LIMIT = 50;

function getIngestedProductMock(
  siteId: string,
  productId: string,
): Promise<IngestedProductSchema> {
  return Promise.resolve(resolveMockIngestedProduct(siteId, productId));
}

async function getIngestedProductReal(
  siteId: string,
  productId: string,
): Promise<IngestedProductSchema> {
  const response = await withJWTClient((client) =>
    client.get<IngestedProductSchema>(
      `${API_BASE_URL}/${siteId}/products/${productId}/`,
      {
        params: {
          includes: "custom_fields,benefit_statements,features,characteristics",
        },
      },
    ),
  );
  return response.data;
}

/**
 * GET /{site_id}/products/{product_id}/
 * `product_id` is the route `skuId` until the backend specifies otherwise (FOG-1250).
 */
export function getIngestedProduct(
  siteId: string,
  productId: string,
): Promise<IngestedProductSchema> {
  return shouldMockCatalog()
    ? getIngestedProductMock(siteId, productId)
    : getIngestedProductReal(siteId, productId);
}

async function getIngestedVariantReal(
  siteId: string,
  variantId: string,
): Promise<IngestedVariantSchema> {
  const response = await withJWTClient((client) =>
    client.get<IngestedVariantSchema>(
      `${API_BASE_URL}/${siteId}/variants/${variantId}/`,
    ),
  );
  return response.data;
}

/** GET /{site_id}/variants/{variant_id}/ */
export function getIngestedVariant(
  siteId: string,
  variantId: string,
): Promise<IngestedVariantSchema> {
  return shouldMockCatalog()
    ? Promise.resolve(resolveMockIngestedVariant(variantId))
    : getIngestedVariantReal(siteId, variantId);
}

function getCatalogAssetsMock(): Promise<GetCatalogAssetsResponse> {
  return Promise.resolve(resolveMockCatalogAssets());
}

async function getCatalogAssetsReal(
  siteId: string,
  query?: GetCatalogAssetsParams,
): Promise<GetCatalogAssetsResponse> {
  const params = new URLSearchParams();
  const {
    origin,
    type,
    approval_status,
    product_id,
    variant_id,
    next,
    limit = CATALOG_ASSETS_DEFAULT_LIMIT,
  } = query ?? {};

  // List params repeat the key; the catalog doesn't split commas.
  origin?.forEach((o) => params.append("origin", o));
  type?.forEach((t) => params.append("type", t));
  if (approval_status?.length) {
    approval_status.forEach((s) => params.append("approval_status", s));
  }
  if (product_id) params.append("product_id", product_id);
  if (variant_id) params.append("variant_id", variant_id);
  if (next !== undefined) params.append("next", next.toString());
  params.append("limit", limit.toString());

  const response = await withJWTClient((client) =>
    client.get<GetCatalogAssetsResponse>(`${API_BASE_URL}/${siteId}/assets/`, {
      params,
      paramsSerializer: { indexes: null },
    }),
  );
  return response.data;
}

/**
 * GET /{site_id}/assets/
 */
export function getCatalogAssets(
  siteId: string,
  query?: GetCatalogAssetsParams,
): Promise<GetCatalogAssetsResponse> {
  return shouldMockCatalog()
    ? getCatalogAssetsMock()
    : getCatalogAssetsReal(siteId, query);
}

function searchProductsMock(
  siteId: string,
  query?: SearchProductsParams,
): Promise<SearchProductsResponse> {
  return Promise.resolve(resolveMockSearchProducts(siteId, query));
}

async function searchProductsReal(
  siteId: string,
  query?: SearchProductsParams,
): Promise<SearchProductsResponse> {
  const params = new URLSearchParams();
  const { product_ids, search, next, forceAll } = query ?? {};

  if (product_ids?.length) {
    product_ids.forEach((id) => params.append("product_ids", id));
  }
  if (search?.trim()) {
    params.append("search", search.trim());
  }
  if (next !== undefined) {
    params.append("next", next.toString());
  }

  if (forceAll) {
    params.append("forceAll", "true");
  } else {
    params.append("forceAll", "false");
  }

  const response = await withJWTClient((client) =>
    client.get<SearchProductsResponse>(`${API_BASE_URL}/${siteId}/products/`, {
      params,
      paramsSerializer: { indexes: null },
    }),
  );
  return response.data;
}

/**
 * GET /{site_id}/products/
 * Search or list ingested products; supports optional filters and pagination.
 */
export function searchProducts(
  siteId: string,
  query?: SearchProductsParams,
): Promise<SearchProductsResponse> {
  return shouldMockCatalog()
    ? searchProductsMock(siteId, query)
    : searchProductsReal(siteId, query);
}

function getProductCardsMock(
  siteId: string,
  productIds: string[],
): Promise<GetProductCardsResponse> {
  return Promise.resolve(resolveMockProductCards(siteId, productIds));
}

async function getProductCardsReal(
  siteId: string,
  productIds: string[],
): Promise<GetProductCardsResponse> {
  const params = new URLSearchParams();
  productIds.forEach((id) => params.append("product_ids", id));

  const response = await withJWTClient((client) =>
    client.get<GetProductCardsResponse>(
      `${API_BASE_URL}/${siteId}/products/cards/`,
      { params, paramsSerializer: { indexes: null } },
    ),
  );
  return response.data;
}

/**
 * GET /{site_id}/products/cards/?product_ids=abc&product_ids=def
 * Lightweight batch product lookup (title, image, price) for card displays.
 */
export function getProductCards(
  siteId: string,
  productIds: string[],
): Promise<GetProductCardsResponse> {
  return shouldMockCatalog()
    ? getProductCardsMock(siteId, productIds)
    : getProductCardsReal(siteId, productIds);
}

async function listProductCardsReal(
  siteId: string,
  params?: ListProductCardsParams,
): Promise<ListProductCardsResponse> {
  const { next, collection, category, tag, sort_by } = params ?? {};
  const response = await withJWTClient((client) =>
    client.get<ListProductCardsResponse>(
      `${API_BASE_URL}/${siteId}/products/cards/`,
      {
        params: {
          count: true,
          ...(next !== undefined ? { next } : {}),
          ...(collection ? { collection } : {}),
          ...(category ? { category } : {}),
          ...(tag ? { tag } : {}),
          ...(sort_by ? { sort_by } : {}),
        },
      },
    ),
  );
  return response.data;
}

export function listProductCards(
  siteId: string,
  params?: ListProductCardsParams,
): Promise<ListProductCardsResponse> {
  return listProductCardsReal(siteId, params);
}

export async function listMediaLibraryAssets(
  siteId: string,
  params: MediaLibraryListParams = {},
): Promise<MediaLibraryListResponse> {
  if (shouldMockCatalog()) {
    return resolveMockMediaLibrary(params);
  }

  const search = new URLSearchParams();
  const q = params.q?.trim();
  if (q) search.append("q", q);
  if (params.collection) search.append("collection", params.collection);
  if (params.category) search.append("category", params.category);
  if (params.tag) search.append("tag", params.tag);
  for (const t of params.type ?? []) search.append("type", t);
  for (const s of params.approval_status ?? []) {
    search.append("approval_status", s);
  }
  if (params.sort_by) search.append("sort_by", params.sort_by);
  if (params.next !== undefined) search.append("next", String(params.next));
  if (params.limit !== undefined) search.append("limit", String(params.limit));

  const response = await withJWTClient((client) =>
    client.get<unknown>(`${API_BASE_URL}/${siteId}/assets/library/`, {
      params: search,
    }),
  );
  return mediaLibraryListResponseSchema.parse(response.data);
}

const catalogSearchResponseSchema = z.object({
  results: z
    .array(
      z
        .object({
          vendor_product_id: z.string().default(""),
          title: z.string().default(""),
          skus: z.array(z.string()).default([]),
        })
        .passthrough(),
    )
    .default([]),
});

/**
 * GET /{site_id}/products/search/
 * Rows are deduped to one per product; `skus` carries every matching variant
 * SKU on the row (ProductCatalog#191) and is empty on older deploys.
 */
export async function searchCatalog(
  siteId: string,
  q: string,
  limit?: number,
): Promise<CatalogSearchResponse> {
  if (shouldMockCatalog()) {
    return resolveMockSearchCatalog(siteId, q);
  }

  const params = new URLSearchParams();
  if (q.trim()) {
    params.append("q", q.trim());
  } else {
    params.append("q", "");
  }

  if (limit !== undefined) {
    params.append("limit", limit.toString());
  }

  const response = await withJWTClient((client) =>
    client.get<unknown>(`${API_BASE_URL}/${siteId}/products/search/`, {
      params,
    }),
  );

  return catalogSearchResponseSchema.parse(response.data);
}

export function toSkuSearchRows(
  data: CatalogSearchResponse | undefined,
): SkuSearchRow[] {
  const seen = new Set<string>();
  const rows: SkuSearchRow[] = [];
  for (const result of data?.results ?? []) {
    for (const rawSku of result.skus ?? []) {
      const sku = rawSku.trim();
      if (!sku || seen.has(sku.toLowerCase())) continue;
      seen.add(sku.toLowerCase());
      rows.push({
        sku,
        title: result.title.trim(),
        vendor_product_id: result.vendor_product_id,
      });
    }
  }
  return rows;
}

const brandSearchResponseSchema = z.object({
  results: z.array(z.string()).default([]),
});

/**
 * GET /{site_id}/brands/search/?q=
 * Ships with FOG-2149; callers must degrade to free-text entry while the route
 * is still missing from a given environment.
 */
export async function searchBrands(
  siteId: string,
  q: string,
): Promise<CatalogHandleSearchResponse> {
  if (shouldMockCatalog()) {
    return resolveMockSearchBrands(q);
  }

  const response = await withJWTClient((client) =>
    client.get<unknown>(`${API_BASE_URL}/${siteId}/brands/search/`, {
      params: { q: q.trim() },
    }),
  );

  return brandSearchResponseSchema.parse(response.data);
}

/**
 * GET /{site_id}/categories/search/
 * Case-insensitive substring match on category handles; an empty `q` returns the
 * first handles alphabetically. The server caps results at 20.
 */
export async function searchCategories(
  siteId: string,
  q: string,
): Promise<CatalogHandleSearchResponse> {
  if (shouldMockCatalog()) {
    return resolveMockSearchCategories(q);
  }

  const response = await withJWTClient((client) =>
    client.get<CatalogHandleSearchResponse>(
      `${API_BASE_URL}/${siteId}/categories/search/`,
      { params: { q: q.trim() } },
    ),
  );

  return response.data;
}

/**
 * GET /{site_id}/collections/search/
 */
export async function searchCollections(
  siteId: string,
  q: string,
): Promise<CatalogHandleSearchResponse> {
  if (shouldMockCatalog()) {
    return resolveMockSearchCollections(q);
  }

  const response = await withJWTClient((client) =>
    client.get<CatalogHandleSearchResponse>(
      `${API_BASE_URL}/${siteId}/collections/search/`,
      { params: { q: q.trim() } },
    ),
  );

  return response.data;
}

/**
 * GET /{site_id}/tags/search/
 */
export async function searchTags(
  siteId: string,
  q: string,
): Promise<CatalogHandleSearchResponse> {
  if (shouldMockCatalog()) {
    return resolveMockSearchTags(q);
  }

  const response = await withJWTClient((client) =>
    client.get<CatalogHandleSearchResponse>(
      `${API_BASE_URL}/${siteId}/tags/search/`,
      { params: { q: q.trim() } },
    ),
  );

  return response.data;
}

function productGroupsParams(
  params?: ListProductGroupsParams,
): Record<string, number | boolean> {
  const { next, limit, include_image, count } = params ?? {};
  return {
    ...(next !== undefined ? { next } : {}),
    ...(limit !== undefined ? { limit } : {}),
    ...(include_image !== undefined ? { include_image } : {}),
    ...(count !== undefined ? { count } : {}),
  };
}

export async function listCollections(
  siteId: string,
  params?: ListProductGroupsParams,
): Promise<ListCollectionsResponse> {
  const response = await withJWTClient((client) =>
    client.get<ListCollectionsResponse>(
      `${API_BASE_URL}/${siteId}/collections/`,
      { params: productGroupsParams(params) },
    ),
  );
  return response.data;
}

export async function listCategories(
  siteId: string,
  params?: ListProductGroupsParams,
): Promise<ListCategoriesResponse> {
  const response = await withJWTClient((client) =>
    client.get<ListCategoriesResponse>(
      `${API_BASE_URL}/${siteId}/categories/`,
      { params: productGroupsParams(params) },
    ),
  );
  return response.data;
}

export async function listTags(
  siteId: string,
  params?: ListProductGroupsParams,
): Promise<ListTagsResponse> {
  const response = await withJWTClient((client) =>
    client.get<ListTagsResponse>(`${API_BASE_URL}/${siteId}/tags/`, {
      params: productGroupsParams(params),
    }),
  );
  return response.data;
}

export type GetSampleProductsParams = {
  /** How many sample products to return (default 3). */
  sampleLimit?: number;
  /** Request `includes=custom_fields`; only needed by the field-mapping UI. */
  includeCustomFields?: boolean;
};

function getSampleProductsMock(
  siteId: string,
  params: GetSampleProductsParams,
): Promise<SearchProductsResponse> {
  return Promise.resolve(resolveMockSampleProducts(siteId, params.sampleLimit));
}

async function getSampleProductsReal(
  siteId: string,
  { sampleLimit = 3, includeCustomFields = false }: GetSampleProductsParams,
): Promise<SearchProductsResponse> {
  const response = await withJWTClient((client) =>
    client.get<SearchProductsResponse>(
      `${API_BASE_URL}/${siteId}/products/sample/`,
      {
        params: {
          ...(includeCustomFields ? { includes: "custom_fields" } : {}),
          sample_limit: sampleLimit,
        },
      },
    ),
  );
  return response.data;
}

/**
 * GET /{site_id}/products/sample/
 * Returns a small representative set of ingested products for the site. Used to
 * surface example source values without fetching a specific variant.
 */
export function getSampleProducts(
  siteId: string,
  params: GetSampleProductsParams = {},
): Promise<SearchProductsResponse> {
  return shouldMockCatalog()
    ? getSampleProductsMock(siteId, params)
    : getSampleProductsReal(siteId, params);
}

function getProductReviewsMock(
  siteId: string,
  params: GetProductReviewsParams,
): Promise<GetProductReviewsResponse> {
  return Promise.resolve(resolveMockProductReviews(siteId, params));
}

async function getProductReviewsReal(
  siteId: string,
  query: GetProductReviewsParams,
): Promise<GetProductReviewsResponse> {
  const params = new URLSearchParams();
  const { ids, limit, source, min_score, verified_buyer, has_user, sort_by } =
    query;

  params.append("ids", ids.join(","));
  params.append("limit", String(limit));
  if (source?.length) params.append("source", source.join(","));
  if (min_score !== undefined) params.append("min_score", String(min_score));
  if (verified_buyer !== undefined) {
    params.append("verified_buyer", String(verified_buyer));
  }
  if (has_user !== undefined) params.append("has_user", String(has_user));
  if (sort_by !== undefined) params.append("sort_by", sort_by);

  const response = await withJWTClient((client) =>
    client.get<GetProductReviewsResponse>(
      `${API_BASE_URL}/${siteId}/products/reviews/`,
      { params },
    ),
  );
  return response.data;
}

/**
 * GET /{site_id}/products/reviews/
 */
export function getProductReviews(
  siteId: string,
  query: GetProductReviewsParams,
): Promise<GetProductReviewsResponse> {
  return shouldMockCatalog()
    ? getProductReviewsMock(siteId, query)
    : getProductReviewsReal(siteId, query);
}

function getFieldMappingConfigMock(
  siteId: string,
  vendorProductId: string,
): Promise<FieldMappingConfigResponse> {
  return Promise.resolve(
    resolveMockFieldMappingConfig(siteId, vendorProductId),
  );
}

async function getFieldMappingConfigReal(
  siteId: string,
  vendorProductId: string,
): Promise<FieldMappingConfigResponse> {
  const response = await withJWTClient((client) =>
    client.get<FieldMappingConfigResponse>(
      `${API_BASE_URL}/${siteId}/field_mapping_config/${vendorProductId}/`,
    ),
  );
  return response.data;
}

/**
 * PATCH /{site_id}/products/{product_id}/override/
 * Applies user-managed overrides without mutating the ingested source row.
 * Returns the merged effective product.
 */
export async function patchProductOverride(
  siteId: string,
  productId: string,
  patch: PatchProductOverrideRequest,
): Promise<IngestedProductSchema> {
  const response = await withJWTClient((client) =>
    client.patch<IngestedProductSchema>(
      `${API_BASE_URL}/${siteId}/products/${productId}/override/`,
      patch,
    ),
  );
  return response.data;
}

/**
 * POST /{site_id}/products/{variant_id}/override/reset/
 * Removes user-managed overrides for the given field paths, restoring ingested values.
 * Returns the merged effective product.
 */
export async function resetProductOverride(
  siteId: string,
  productId: string,
  request: ResetProductOverrideRequest,
): Promise<IngestedProductSchema> {
  const response = await withJWTClient((client) =>
    client.post<IngestedProductSchema>(
      `${API_BASE_URL}/${siteId}/products/${productId}/override/reset/`,
      request,
    ),
  );
  return response.data;
}

/**
 * GET /{site_id}/field_mapping_config/{vendor_product_id}/
 */
export function getFieldMappingConfig(
  siteId: string,
  vendorProductId: string,
): Promise<FieldMappingConfigResponse> {
  return shouldMockCatalog()
    ? getFieldMappingConfigMock(siteId, vendorProductId)
    : getFieldMappingConfigReal(siteId, vendorProductId);
}

function updateFieldMappingConfigMock(
  siteId: string,
  request: UpdateFieldMappingConfigRequest,
): Promise<FieldMappingConfigResponse> {
  return Promise.resolve(resolveMockUpdateFieldMappingConfig(siteId, request));
}

async function updateFieldMappingConfigReal(
  siteId: string,
  request: UpdateFieldMappingConfigRequest,
): Promise<FieldMappingConfigResponse> {
  const response = await withJWTClient((client) =>
    client.patch<FieldMappingConfigResponse>(
      `${API_BASE_URL}/${siteId}/field_mapping_config/`,
      request,
    ),
  );
  return response.data;
}

/**
 * PATCH /{site_id}/field_mapping_config/
 * Creates, updates, or removes site-level mappings from source custom fields to
 * canonical target fields. Mappings not listed in `updates` are preserved.
 * Returns the full merged {@link FieldMappingConfigResponse}.
 */
export function updateFieldMappingConfig(
  siteId: string,
  request: UpdateFieldMappingConfigRequest,
): Promise<FieldMappingConfigResponse> {
  return shouldMockCatalog()
    ? updateFieldMappingConfigMock(siteId, request)
    : updateFieldMappingConfigReal(siteId, request);
}

export const skuStoreKeys = {
  all: ["skuStore"] as const,
  ingestedProduct: (siteId: string, productId: string) =>
    [...skuStoreKeys.all, "ingestedProduct", siteId, productId] as const,
  ingestedVariant: (siteId: string, variantId: string) =>
    [...skuStoreKeys.all, "ingestedVariant", siteId, variantId] as const,
  catalogAssets: (siteId: string, params?: GetCatalogAssetsParams) =>
    [...skuStoreKeys.all, "catalogAssets", siteId, params ?? {}] as const,
  mediaLibrary: (siteId: string, params?: MediaLibraryListParams) =>
    [...skuStoreKeys.all, "mediaLibrary", siteId, params ?? {}] as const,
  productReviews: (siteId: string, params: GetProductReviewsParams) =>
    [...skuStoreKeys.all, "productReviews", siteId, params] as const,
  searchProducts: (siteId: string, params?: SearchProductsParams) =>
    [...skuStoreKeys.all, "searchProducts", siteId, params ?? {}] as const,
  productCards: (siteId: string, productIds: string[]) =>
    [...skuStoreKeys.all, "productCards", siteId, productIds] as const,
  /** Prefix for every product-card list on a site (any filter / sort). */
  siteProductCardLists: (siteId: string) =>
    [...skuStoreKeys.all, "listProductCards", siteId] as const,
  listProductCards: (
    siteId: string,
    filter?: ProductCardsTaxonomyFilter,
    sortBy?: ProductCardSortBy,
  ) =>
    [
      ...skuStoreKeys.siteProductCardLists(siteId),
      { ...filter, sort_by: sortBy },
    ] as const,
  collections: (siteId: string, params?: ListProductGroupsParams) =>
    [...skuStoreKeys.all, "collections", siteId, params ?? {}] as const,
  categories: (siteId: string, params?: ListProductGroupsParams) =>
    [...skuStoreKeys.all, "categories", siteId, params ?? {}] as const,
  tags: (siteId: string, params?: ListProductGroupsParams) =>
    [...skuStoreKeys.all, "tags", siteId, params ?? {}] as const,
  sampleProducts: (siteId: string, params: GetSampleProductsParams) =>
    [...skuStoreKeys.all, "sampleProducts", siteId, params] as const,
  searchCatalog: (siteId: string, q: string) =>
    [...skuStoreKeys.all, "searchCatalog", siteId, q] as const,
  searchCategories: (siteId: string, q: string) =>
    [...skuStoreKeys.all, "searchCategories", siteId, q] as const,
  searchCollections: (siteId: string, q: string) =>
    [...skuStoreKeys.all, "searchCollections", siteId, q] as const,
  searchTags: (siteId: string, q: string) =>
    [...skuStoreKeys.all, "searchTags", siteId, q] as const,
  searchBrands: (siteId: string, q: string) =>
    [...skuStoreKeys.all, "searchBrands", siteId, q] as const,
  fieldMappingConfig: (siteId: string, vendorProductId: string) =>
    [
      ...skuStoreKeys.all,
      "fieldMappingConfig",
      siteId,
      vendorProductId,
    ] as const,
  siteFieldMappingConfig: (siteId: string) =>
    [...skuStoreKeys.all, "fieldMappingConfig", siteId, "__site__"] as const,
  mappingFields: (siteId: string) =>
    [...skuStoreKeys.all, "mappingFields", siteId] as const,
  characteristics: (siteId: string) =>
    [...skuStoreKeys.all, "characteristics", siteId] as const,
  mappingFieldValues: (siteId: string, fieldName: string) =>
    [...skuStoreKeys.all, "mappingFieldValues", siteId, fieldName] as const,
  allMappingFieldValues: (siteId: string) =>
    [...skuStoreKeys.all, "allMappingFieldValues", siteId] as const,
  productAssociations: (
    siteId: string,
    productId: string,
    params: GetProductAssociationsParams,
  ) =>
    [
      ...skuStoreKeys.all,
      "productAssociations",
      siteId,
      productId,
      params,
    ] as const,
  productEngagement: (
    siteId: string,
    productId: string,
    params?: GetProductEngagementParams,
  ) =>
    [
      ...skuStoreKeys.all,
      "productEngagement",
      siteId,
      productId,
      params ?? {},
    ] as const,
};

export const ingestedProductQueryOptions = (
  siteId: string,
  productId: string,
) =>
  ({
    queryKey: skuStoreKeys.ingestedProduct(siteId, productId),
    queryFn: () => getIngestedProduct(siteId, productId),
    enabled: Boolean(siteId && productId),
  }) satisfies Pick<
    UseQueryOptions<IngestedProductSchema, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

export const ingestedVariantQueryOptions = (
  siteId: string,
  variantId: string,
) =>
  ({
    queryKey: skuStoreKeys.ingestedVariant(siteId, variantId),
    queryFn: () => getIngestedVariant(siteId, variantId),
    enabled: Boolean(siteId && variantId),
  }) satisfies Pick<
    UseQueryOptions<IngestedVariantSchema, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

export const catalogAssetsQueryOptions = (
  siteId: string,
  params?: GetCatalogAssetsParams,
) =>
  ({
    queryKey: skuStoreKeys.catalogAssets(siteId, params),
    queryFn: () => getCatalogAssets(siteId, params),
    enabled: Boolean(siteId),
  }) satisfies Pick<
    UseQueryOptions<GetCatalogAssetsResponse, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

export const productReviewsQueryOptions = (
  siteId: string,
  params: GetProductReviewsParams,
) =>
  ({
    queryKey: skuStoreKeys.productReviews(siteId, params),
    queryFn: () => getProductReviews(siteId, params),
    enabled: Boolean(siteId && params.ids.length > 0 && params.limit > 0),
  }) satisfies Pick<
    UseQueryOptions<GetProductReviewsResponse, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

export function useCatalogAssets(
  siteId: string,
  params?: GetCatalogAssetsParams,
  options?: Omit<
    UseQueryOptions<GetCatalogAssetsResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<GetCatalogAssetsResponse, Error> {
  return useQuery<GetCatalogAssetsResponse, Error>({
    ...catalogAssetsQueryOptions(siteId, params),
    ...options,
  });
}

export const sampleProductsQueryOptions = (
  siteId: string,
  params: GetSampleProductsParams = {},
) =>
  ({
    queryKey: skuStoreKeys.sampleProducts(siteId, params),
    queryFn: () => getSampleProducts(siteId, params),
    enabled: Boolean(siteId),
  }) satisfies Pick<
    UseQueryOptions<SearchProductsResponse, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

export function useSampleProducts(
  siteId: string,
  params: GetSampleProductsParams = {},
  options?: Omit<
    UseQueryOptions<SearchProductsResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<SearchProductsResponse, Error> {
  return useQuery<SearchProductsResponse, Error>({
    ...sampleProductsQueryOptions(siteId, params),
    ...options,
  });
}

export const catalogSearchQueryOptions = (siteId: string, q: string) =>
  ({
    queryKey: skuStoreKeys.searchCatalog(siteId, q),
    queryFn: () => searchCatalog(siteId, q),
    enabled: Boolean(siteId),
  }) satisfies Pick<
    UseQueryOptions<CatalogSearchResponse, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

const PRODUCT_CARDS_MAX_IDS = 100;

export const productCardsQueryOptions = (
  siteId: string,
  productIds: string[],
) =>
  ({
    queryKey: skuStoreKeys.productCards(siteId, productIds),
    queryFn: () => getProductCards(siteId, productIds),
    enabled: Boolean(siteId) && productIds.length > 0,
  }) satisfies Pick<
    UseQueryOptions<GetProductCardsResponse, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

export function useProductCards(
  siteId: string,
  productIds: string[],
  options?: Omit<
    UseQueryOptions<GetProductCardsResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<GetProductCardsResponse, Error> {
  return useQuery<GetProductCardsResponse, Error>({
    ...productCardsQueryOptions(siteId, productIds),
    ...options,
  });
}

export function useProductCardsChunked(
  siteId: string,
  productIds: string[],
): UseQueryResult<GetProductCardsResponse, Error>[] {
  const chunks: string[][] = [];
  for (let i = 0; i < productIds.length; i += PRODUCT_CARDS_MAX_IDS) {
    chunks.push(productIds.slice(i, i + PRODUCT_CARDS_MAX_IDS));
  }
  return useQueries({
    queries: chunks.map((chunk) => productCardsQueryOptions(siteId, chunk)),
  });
}

export function useInfiniteListProductCards(
  siteId: string,
  options?: {
    enabled?: boolean;
    filter?: ProductCardsTaxonomyFilter;
    sortBy?: ProductCardSortBy;
  },
): UseInfiniteQueryResult<InfiniteData<ListProductCardsResponse>, Error> {
  const filter = options?.filter;
  const sortBy = options?.sortBy;
  return useInfiniteQuery({
    queryKey: [
      ...skuStoreKeys.listProductCards(siteId, filter, sortBy),
      "infinite",
    ],
    queryFn: ({ pageParam }: { pageParam: number | undefined }) =>
      listProductCards(siteId, { next: pageParam, ...filter, sort_by: sortBy }),
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    initialPageParam: undefined as number | undefined,
    enabled: Boolean(siteId) && (options?.enabled ?? true),
  });
}

export function useInfiniteMediaLibraryAssets(
  siteId: string,
  options?: { enabled?: boolean; params?: MediaLibraryListParams },
): UseInfiniteQueryResult<InfiniteData<MediaLibraryListResponse>, Error> {
  const params = options?.params;
  return useInfiniteQuery({
    queryKey: [...skuStoreKeys.mediaLibrary(siteId, params), "infinite"],
    queryFn: ({ pageParam }: { pageParam: number | undefined }) =>
      listMediaLibraryAssets(siteId, { ...params, next: pageParam }),
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    initialPageParam: params?.next,
    enabled: Boolean(siteId) && (options?.enabled ?? true),
  });
}

export function useCatalogSearch(
  siteId: string,
  q: string,
  options?: Omit<
    UseQueryOptions<CatalogSearchResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<CatalogSearchResponse, Error> {
  return useQuery<CatalogSearchResponse, Error>({
    ...catalogSearchQueryOptions(siteId, q),
    ...options,
  });
}

export function useCatalogSearches(
  siteId: string,
  queries: string[],
): UseQueryResult<CatalogSearchResponse, Error>[] {
  return useQueries({
    queries: queries.map((q) => ({
      ...catalogSearchQueryOptions(siteId, q),
      enabled: Boolean(siteId) && q.trim().length > 0,
    })),
  });
}

export function useSearchCategories(
  siteId: string,
  q: string,
  options?: { enabled?: boolean },
): UseQueryResult<CatalogHandleSearchResponse, Error> {
  return useQuery<CatalogHandleSearchResponse, Error>({
    queryKey: skuStoreKeys.searchCategories(siteId, q),
    queryFn: () => searchCategories(siteId, q),
    enabled: Boolean(siteId) && (options?.enabled ?? true),
  });
}

export function useSearchCollections(
  siteId: string,
  q: string,
): UseQueryResult<CatalogHandleSearchResponse, Error> {
  return useQuery<CatalogHandleSearchResponse, Error>({
    queryKey: skuStoreKeys.searchCollections(siteId, q),
    queryFn: () => searchCollections(siteId, q),
    enabled: Boolean(siteId),
  });
}

export function useSearchTags(
  siteId: string,
  q: string,
): UseQueryResult<CatalogHandleSearchResponse, Error> {
  return useQuery<CatalogHandleSearchResponse, Error>({
    queryKey: skuStoreKeys.searchTags(siteId, q),
    queryFn: () => searchTags(siteId, q),
    enabled: Boolean(siteId),
  });
}

export const collectionsQueryOptions = (
  siteId: string,
  params?: ListProductGroupsParams,
) =>
  ({
    queryKey: skuStoreKeys.collections(siteId, params),
    queryFn: () => listCollections(siteId, params),
    enabled: Boolean(siteId),
  }) satisfies Pick<
    UseQueryOptions<ListCollectionsResponse, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

/**
 * GET /{site_id}/collections/ — collection handles with active product counts.
 */
export function useCollections(
  siteId: string,
  params?: ListProductGroupsParams,
  options?: Omit<
    UseQueryOptions<ListCollectionsResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<ListCollectionsResponse, Error> {
  return useQuery<ListCollectionsResponse, Error>({
    ...collectionsQueryOptions(siteId, params),
    ...options,
  });
}

export const categoriesQueryOptions = (
  siteId: string,
  params?: ListProductGroupsParams,
) =>
  ({
    queryKey: skuStoreKeys.categories(siteId, params),
    queryFn: () => listCategories(siteId, params),
    enabled: Boolean(siteId),
  }) satisfies Pick<
    UseQueryOptions<ListCategoriesResponse, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

/**
 * GET /{site_id}/categories/ — category handles with active product counts.
 */
export function useCategories(
  siteId: string,
  params?: ListProductGroupsParams,
  options?: Omit<
    UseQueryOptions<ListCategoriesResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<ListCategoriesResponse, Error> {
  return useQuery<ListCategoriesResponse, Error>({
    ...categoriesQueryOptions(siteId, params),
    ...options,
  });
}

export const tagsQueryOptions = (
  siteId: string,
  params?: ListProductGroupsParams,
) =>
  ({
    queryKey: skuStoreKeys.tags(siteId, params),
    queryFn: () => listTags(siteId, params),
    enabled: Boolean(siteId),
  }) satisfies Pick<
    UseQueryOptions<ListTagsResponse, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

/**
 * GET /{site_id}/tags/ — tag handles with active product counts.
 */
export function useTags(
  siteId: string,
  params?: ListProductGroupsParams,
  options?: Omit<
    UseQueryOptions<ListTagsResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<ListTagsResponse, Error> {
  return useQuery<ListTagsResponse, Error>({
    ...tagsQueryOptions(siteId, params),
    ...options,
  });
}

type InfiniteProductGroupsOptions = {
  params?: Omit<ListProductGroupsParams, "next">;
  enabled?: boolean;
};

/** GET /{site_id}/collections/, paged via `next`. */
export function useInfiniteCollections(
  siteId: string,
  options?: InfiniteProductGroupsOptions,
): UseInfiniteQueryResult<InfiniteData<ListCollectionsResponse>, Error> {
  const params = options?.params;
  return useInfiniteQuery({
    queryKey: [...skuStoreKeys.collections(siteId, params), "infinite"],
    queryFn: ({ pageParam }: { pageParam: number | undefined }) =>
      listCollections(siteId, { ...params, next: pageParam }),
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    initialPageParam: undefined as number | undefined,
    enabled: Boolean(siteId) && (options?.enabled ?? true),
  });
}

/** GET /{site_id}/categories/, paged via `next`. */
export function useInfiniteCategories(
  siteId: string,
  options?: InfiniteProductGroupsOptions,
): UseInfiniteQueryResult<InfiniteData<ListCategoriesResponse>, Error> {
  const params = options?.params;
  return useInfiniteQuery({
    queryKey: [...skuStoreKeys.categories(siteId, params), "infinite"],
    queryFn: ({ pageParam }: { pageParam: number | undefined }) =>
      listCategories(siteId, { ...params, next: pageParam }),
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    initialPageParam: undefined as number | undefined,
    enabled: Boolean(siteId) && (options?.enabled ?? true),
  });
}

/** GET /{site_id}/tags/, paged via `next`. */
export function useInfiniteTags(
  siteId: string,
  options?: InfiniteProductGroupsOptions,
): UseInfiniteQueryResult<InfiniteData<ListTagsResponse>, Error> {
  const params = options?.params;
  return useInfiniteQuery({
    queryKey: [...skuStoreKeys.tags(siteId, params), "infinite"],
    queryFn: ({ pageParam }: { pageParam: number | undefined }) =>
      listTags(siteId, { ...params, next: pageParam }),
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    initialPageParam: undefined as number | undefined,
    enabled: Boolean(siteId) && (options?.enabled ?? true),
  });
}

export function useSearchBrands(
  siteId: string,
  q: string,
  options?: Omit<
    UseQueryOptions<CatalogHandleSearchResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<CatalogHandleSearchResponse, Error> {
  return useQuery<CatalogHandleSearchResponse, Error>({
    queryKey: skuStoreKeys.searchBrands(siteId, q),
    queryFn: () => searchBrands(siteId, q),
    enabled: Boolean(siteId),
    retry: false,
    ...options,
  });
}

export function useProductReviews(
  siteId: string,
  params: GetProductReviewsParams,
  options?: Omit<
    UseQueryOptions<GetProductReviewsResponse, Error>,
    "queryKey" | "queryFn" | "enabled"
  >,
): UseQueryResult<GetProductReviewsResponse, Error> {
  return useQuery<GetProductReviewsResponse, Error>({
    ...productReviewsQueryOptions(siteId, params),
    ...options,
  });
}

export function useIngestedProduct(
  siteId: string,
  productId: string,
  options?: Omit<
    UseQueryOptions<IngestedProductSchema, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<IngestedProductSchema, Error> {
  return useQuery<IngestedProductSchema, Error>({
    ...ingestedProductQueryOptions(siteId, productId),
    ...options,
  });
}

export function useIngestedVariant(
  siteId: string,
  variantId: string,
): UseQueryResult<IngestedVariantSchema, Error> {
  return useQuery<IngestedVariantSchema, Error>(
    ingestedVariantQueryOptions(siteId, variantId),
  );
}

/**
 * Same query as {@link useIngestedProduct}, plus a user-visible error snackbar.
 * Prefer mounting once in the SKU PDP layout so child routes can use {@link useIngestedProduct}
 * without duplicating snackbars.
 */
export function useIngestedProductWithErrorSnackbar(
  siteId: string,
  productId: string,
  options?: Omit<
    UseQueryOptions<IngestedProductSchema, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<IngestedProductSchema, Error> {
  const query = useIngestedProduct(siteId, productId, options);
  useErrorSnackbarOnce(query.error, "Failed to load product");
  return query;
}

/** Toasts each distinct error message once, until the error clears. */
function useErrorSnackbarOnce(error: Error | null, fallbackMessage: string) {
  const lastErrorMessageRef = useRef<string>("");

  useEffect(() => {
    if (!error) {
      lastErrorMessageRef.current = "";
      return;
    }

    const msg = getBackendErrorMessage(error);
    if (msg && msg !== lastErrorMessageRef.current) {
      enqueueErrorSnackbar(error, fallbackMessage);
      lastErrorMessageRef.current = msg;
    }
  }, [error, fallbackMessage]);
}

// Module-level so the error keeps one identity across renders.
const variantOfOtherProductError = new Error(
  "This variant doesn't belong to this product.",
);

/**
 * {@link useIngestedVariant} scoped to its parent: a variant of another product
 * is reported as `error` and never returned. Undefined until the parent loads.
 */
export function useProductVariant(
  siteId: string,
  parentProductId: string | undefined,
  variantId: string | undefined,
): { variant: IngestedVariantSchema | undefined; error: Error | null } {
  const { data, error } = useIngestedVariant(siteId, variantId ?? "");
  if (error) return { variant: undefined, error };
  if (!data || !parentProductId) return { variant: undefined, error: null };
  return data.vendor_product_id === parentProductId
    ? { variant: data, error: null }
    : { variant: undefined, error: variantOfOtherProductError };
}

/** Same as {@link useProductVariant}, plus an error snackbar. Mount once, in the PDP layout. */
export function useProductVariantWithErrorSnackbar(
  siteId: string,
  parentProductId: string | undefined,
  variantId: string | undefined,
): ReturnType<typeof useProductVariant> {
  const result = useProductVariant(siteId, parentProductId, variantId);
  useErrorSnackbarOnce(result.error, "Failed to load variant");
  return result;
}

export const fieldMappingConfigQueryOptions = (
  siteId: string,
  vendorProductId: string,
) =>
  ({
    queryKey: skuStoreKeys.fieldMappingConfig(siteId, vendorProductId),
    queryFn: () => getFieldMappingConfig(siteId, vendorProductId),
    enabled: Boolean(siteId && vendorProductId),
  }) satisfies Pick<
    UseQueryOptions<FieldMappingConfigResponse, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

export function useFieldMappingConfig(
  siteId: string,
  vendorProductId: string,
  options?: Omit<
    UseQueryOptions<FieldMappingConfigResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<FieldMappingConfigResponse, Error> {
  return useQuery<FieldMappingConfigResponse, Error>({
    ...fieldMappingConfigQueryOptions(siteId, vendorProductId),
    ...options,
  });
}

/**
 * Refreshes every product-card list on the site (any filter or sort). Lists
 * cache for the app-wide staleTime, so a write that changes what a card shows
 * (title, price, stock) calls this.
 *
 * Mapping, characteristic, and story writes do not: the server recomputes
 * stored SKU Scores only on product writes, engagement ingest, and the
 * nightly backfill, and stories are not a score input.
 */
export function invalidateSiteProductCardLists(
  queryClient: QueryClient,
  siteId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: skuStoreKeys.siteProductCardLists(siteId),
  });
}

/**
 * Mutation for PATCH /{site_id}/field_mapping_config/.
 * On success, updates the cached config for the given product and invalidates
 * any other cached field mapping config queries for the site.
 */
export function useUpdateFieldMappingConfig(
  siteId: string,
  vendorProductId: string,
  options?: Omit<
    UseMutationOptions<
      FieldMappingConfigResponse,
      Error,
      UpdateFieldMappingConfigRequest
    >,
    "mutationFn"
  >,
): UseMutationResult<
  FieldMappingConfigResponse,
  Error,
  UpdateFieldMappingConfigRequest
> {
  const queryClient = useQueryClient();
  return useMutation<
    FieldMappingConfigResponse,
    Error,
    UpdateFieldMappingConfigRequest
  >({
    mutationFn: (request) => updateFieldMappingConfig(siteId, request),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.setQueryData(
        skuStoreKeys.fieldMappingConfig(siteId, vendorProductId),
        data,
      );
      void queryClient.invalidateQueries({
        queryKey: [...skuStoreKeys.all, "fieldMappingConfig", siteId],
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

function getSiteFieldMappingConfigMock(
  siteId: string,
): Promise<FieldMappingConfigResponse> {
  // The mock config is site-scoped; the demo product id seeds the full fixture.
  return Promise.resolve(resolveMockFieldMappingConfig(siteId, "1234567890"));
}

async function getSiteFieldMappingConfigReal(
  siteId: string,
): Promise<FieldMappingConfigResponse> {
  const response = await withJWTClient((client) =>
    client.get<FieldMappingConfigResponse>(
      `${API_BASE_URL}/${siteId}/field_mapping_config/`,
    ),
  );
  return response.data;
}

/**
 * GET /{site_id}/field_mapping_config/
 * Brand-level view of the site's field mapping config, spanning every source
 * field the site has ingested (not scoped to a single variant).
 */
export function getSiteFieldMappingConfig(
  siteId: string,
): Promise<FieldMappingConfigResponse> {
  return shouldMockCatalog()
    ? getSiteFieldMappingConfigMock(siteId)
    : getSiteFieldMappingConfigReal(siteId);
}

export const siteFieldMappingConfigQueryOptions = (siteId: string) =>
  ({
    queryKey: skuStoreKeys.siteFieldMappingConfig(siteId),
    queryFn: () => getSiteFieldMappingConfig(siteId),
    enabled: Boolean(siteId),
  }) satisfies Pick<
    UseQueryOptions<FieldMappingConfigResponse, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

export function useSiteFieldMappingConfig(
  siteId: string,
  options?: Omit<
    UseQueryOptions<FieldMappingConfigResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<FieldMappingConfigResponse, Error> {
  return useQuery<FieldMappingConfigResponse, Error>({
    ...siteFieldMappingConfigQueryOptions(siteId),
    ...options,
  });
}

/**
 * Mutation for PATCH /{site_id}/field_mapping_config/ at the brand level.
 * The update endpoint is already site-level; on success this refreshes the
 * brand-level cache and invalidates every per-variant config for the site.
 * Per-call side effects (snackbars, resets) belong at the `mutate(vars, {
 * onSuccess })` call site.
 */
export function useUpdateSiteFieldMappingConfig(
  siteId: string,
): UseMutationResult<
  FieldMappingConfigResponse,
  Error,
  UpdateFieldMappingConfigRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request) => updateFieldMappingConfig(siteId, request),
    onSuccess: (data) => {
      queryClient.setQueryData(
        skuStoreKeys.siteFieldMappingConfig(siteId),
        data,
      );
      void queryClient.invalidateQueries({
        queryKey: [...skuStoreKeys.all, "fieldMappingConfig", siteId],
      });
    },
  });
}

function getMappingFieldsMock(): Promise<MappingFieldsResponse> {
  return Promise.resolve(resolveMockMappingFields());
}

async function getMappingFieldsReal(
  siteId: string,
): Promise<MappingFieldsResponse> {
  const response = await withJWTClient((client) =>
    client.get<MappingFieldsResponse>(
      `${API_BASE_URL}/${siteId}/mapping_fields/`,
    ),
  );
  return response.data;
}

/**
 * GET /{site_id}/mapping_fields/
 * Returns the list of canonical internal fields available as mapping targets.
 */
export function getMappingFields(
  siteId: string,
): Promise<MappingFieldsResponse> {
  return shouldMockCatalog()
    ? getMappingFieldsMock()
    : getMappingFieldsReal(siteId);
}

export const mappingFieldsQueryOptions = (siteId: string) =>
  ({
    queryKey: skuStoreKeys.mappingFields(siteId),
    queryFn: () => getMappingFields(siteId),
    enabled: Boolean(siteId),
    staleTime: 5 * 60 * 1000,
  }) satisfies Pick<
    UseQueryOptions<MappingFieldsResponse, Error>,
    "queryKey" | "queryFn" | "enabled" | "staleTime"
  >;

export function useMappingFields(
  siteId: string,
  options?: Omit<
    UseQueryOptions<MappingFieldsResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<MappingFieldsResponse, Error> {
  return useQuery<MappingFieldsResponse, Error>({
    ...mappingFieldsQueryOptions(siteId),
    ...options,
  });
}

function getCharacteristicsMock(): Promise<CharacteristicsResponse> {
  return Promise.resolve(resolveMockCharacteristics());
}

async function getCharacteristicsReal(
  siteId: string,
): Promise<CharacteristicsResponse> {
  const response = await withJWTClient((client) =>
    client.get<CharacteristicsResponse>(
      `${API_BASE_URL}/${siteId}/characteristics/`,
    ),
  );
  return response.data;
}

/**
 * GET /{site_id}/characteristics/
 * Lists the canonical field (`characteristic`) definitions plus their
 * configured `allowed_values` vocabulary and usage counts.
 */
export function getCharacteristics(
  siteId: string,
): Promise<CharacteristicsResponse> {
  return shouldMockCatalog()
    ? getCharacteristicsMock()
    : getCharacteristicsReal(siteId);
}

function updateCharacteristicsMock(
  request: UpdateCharacteristicsRequest,
): Promise<CharacteristicsResponse> {
  return Promise.resolve(resolveMockUpdateCharacteristics(request));
}

async function updateCharacteristicsReal(
  siteId: string,
  request: UpdateCharacteristicsRequest,
): Promise<CharacteristicsResponse> {
  const response = await withJWTClient((client) =>
    client.patch<CharacteristicsResponse>(
      `${API_BASE_URL}/${siteId}/characteristics/`,
      request,
    ),
  );
  return response.data;
}

/**
 * PATCH /{site_id}/characteristics/
 * Creates/updates/removes canonical field definitions. Entries are keyed by the
 * immutable `key`; unknown keys are created. Returns the updated definitions.
 */
export function updateCharacteristics(
  siteId: string,
  request: UpdateCharacteristicsRequest,
): Promise<CharacteristicsResponse> {
  return shouldMockCatalog()
    ? updateCharacteristicsMock(request)
    : updateCharacteristicsReal(siteId, request);
}

export const characteristicsQueryOptions = (siteId: string) =>
  ({
    queryKey: skuStoreKeys.characteristics(siteId),
    queryFn: () => getCharacteristics(siteId),
    enabled: Boolean(siteId),
    staleTime: 5 * 60 * 1000,
  }) satisfies Pick<
    UseQueryOptions<CharacteristicsResponse, Error>,
    "queryKey" | "queryFn" | "enabled" | "staleTime"
  >;

export function useCharacteristics(
  siteId: string,
  options?: Omit<
    UseQueryOptions<CharacteristicsResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<CharacteristicsResponse, Error> {
  return useQuery<CharacteristicsResponse, Error>({
    ...characteristicsQueryOptions(siteId),
    ...options,
  });
}

/**
 * PATCH /{site_id}/characteristics/
 * Invalidates the characteristics list, the mapping-fields list, and the
 * aggregate mapping-field values (a vocabulary change affects all three) on
 * success.
 */
export function useUpdateCharacteristics(
  siteId: string,
): UseMutationResult<
  CharacteristicsResponse,
  Error,
  UpdateCharacteristicsRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request) => updateCharacteristics(siteId, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: skuStoreKeys.characteristics(siteId),
      });
      void queryClient.invalidateQueries({
        queryKey: skuStoreKeys.mappingFields(siteId),
      });
      void queryClient.invalidateQueries({
        queryKey: skuStoreKeys.allMappingFieldValues(siteId),
      });
    },
  });
}

function getMappingFieldValuesMock(
  fieldName: string,
): Promise<MappingFieldValuesResponse> {
  return Promise.resolve(resolveMockMappingFieldValues(fieldName));
}

async function getMappingFieldValuesReal(
  siteId: string,
  fieldName: string,
): Promise<MappingFieldValuesResponse> {
  const response = await withJWTClient((client) =>
    client.get<MappingFieldValuesResponse>(
      `${API_BASE_URL}/${siteId}/mapping_fields/${encodeURIComponent(fieldName)}/values/`,
    ),
  );
  return response.data;
}

/**
 * GET /{site_id}/mapping_fields/{field_name}/values/
 * Lists the configured vocabulary for a field. `field_name` accepts bare and
 * namespaced (`product:`/`characteristic:`) names; the response echoes the bare
 * name and serves `characteristics_count: null`.
 */
export function getMappingFieldValues(
  siteId: string,
  fieldName: string,
): Promise<MappingFieldValuesResponse> {
  return shouldMockCatalog()
    ? getMappingFieldValuesMock(fieldName)
    : getMappingFieldValuesReal(siteId, fieldName);
}

function updateMappingFieldValuesMock(
  fieldName: string,
  request: UpdateMappingFieldValuesRequest,
): Promise<MappingFieldValuesResponse> {
  return Promise.resolve(
    resolveMockUpdateMappingFieldValues(fieldName, request),
  );
}

async function updateMappingFieldValuesReal(
  siteId: string,
  fieldName: string,
  request: UpdateMappingFieldValuesRequest,
): Promise<MappingFieldValuesResponse> {
  const response = await withJWTClient((client) =>
    client.patch<MappingFieldValuesResponse>(
      `${API_BASE_URL}/${siteId}/mapping_fields/${encodeURIComponent(fieldName)}/values/`,
      request,
    ),
  );
  return response.data;
}

/**
 * PATCH /{site_id}/mapping_fields/{field_name}/values/
 * Replaces a field's configured vocabulary in one call. Positions follow list
 * order; `{ values: [] }` clears. Requires the field to be flagged
 * `has_allowed_values` for a non-empty vocabulary.
 */
export function updateMappingFieldValues(
  siteId: string,
  fieldName: string,
  request: UpdateMappingFieldValuesRequest,
): Promise<MappingFieldValuesResponse> {
  return shouldMockCatalog()
    ? updateMappingFieldValuesMock(fieldName, request)
    : updateMappingFieldValuesReal(siteId, fieldName, request);
}

export const mappingFieldValuesQueryOptions = (
  siteId: string,
  fieldName: string,
) =>
  ({
    queryKey: skuStoreKeys.mappingFieldValues(siteId, fieldName),
    queryFn: () => getMappingFieldValues(siteId, fieldName),
    enabled: Boolean(siteId && fieldName),
    staleTime: 5 * 60 * 1000,
  }) satisfies Pick<
    UseQueryOptions<MappingFieldValuesResponse, Error>,
    "queryKey" | "queryFn" | "enabled" | "staleTime"
  >;

export function useMappingFieldValues(
  siteId: string,
  fieldName: string,
  options?: Omit<
    UseQueryOptions<MappingFieldValuesResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<MappingFieldValuesResponse, Error> {
  return useQuery<MappingFieldValuesResponse, Error>({
    ...mappingFieldValuesQueryOptions(siteId, fieldName),
    ...options,
  });
}

/**
 * PATCH /{site_id}/mapping_fields/{field_name}/values/
 * Invalidates that field's values, the aggregate mapping-field values, and the
 * characteristics list (usage counts are recomputed there) on success.
 */
export function useUpdateMappingFieldValues(
  siteId: string,
  fieldName: string,
): UseMutationResult<
  MappingFieldValuesResponse,
  Error,
  UpdateMappingFieldValuesRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request) =>
      updateMappingFieldValues(siteId, fieldName, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: skuStoreKeys.mappingFieldValues(siteId, fieldName),
      });
      void queryClient.invalidateQueries({
        queryKey: skuStoreKeys.allMappingFieldValues(siteId),
      });
      void queryClient.invalidateQueries({
        queryKey: skuStoreKeys.characteristics(siteId),
      });
    },
  });
}

function getAllMappingFieldValuesMock(): Promise<AllMappingFieldValuesResponse> {
  return Promise.resolve(resolveMockAllMappingFieldValues());
}

async function getAllMappingFieldValuesReal(
  siteId: string,
): Promise<AllMappingFieldValuesResponse> {
  const response = await withJWTClient((client) =>
    client.get<AllMappingFieldValuesResponse>(
      `${API_BASE_URL}/${siteId}/mapping_fields/values/`,
    ),
  );
  return response.data;
}

/**
 * GET /{site_id}/mapping_fields/values/
 * Lists configured vocabularies across fields (`characteristics_count: null`).
 */
export function getAllMappingFieldValues(
  siteId: string,
): Promise<AllMappingFieldValuesResponse> {
  return shouldMockCatalog()
    ? getAllMappingFieldValuesMock()
    : getAllMappingFieldValuesReal(siteId);
}

export const allMappingFieldValuesQueryOptions = (siteId: string) =>
  ({
    queryKey: skuStoreKeys.allMappingFieldValues(siteId),
    queryFn: () => getAllMappingFieldValues(siteId),
    enabled: Boolean(siteId),
    staleTime: 5 * 60 * 1000,
  }) satisfies Pick<
    UseQueryOptions<AllMappingFieldValuesResponse, Error>,
    "queryKey" | "queryFn" | "enabled" | "staleTime"
  >;

export function useAllMappingFieldValues(
  siteId: string,
  options?: Omit<
    UseQueryOptions<AllMappingFieldValuesResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<AllMappingFieldValuesResponse, Error> {
  return useQuery<AllMappingFieldValuesResponse, Error>({
    ...allMappingFieldValuesQueryOptions(siteId),
    ...options,
  });
}

/** First image asset on the product, or the first asset if none are typed as `image`. */
export function getPrimaryProductImageUrl(
  product: IngestedProductSchema | undefined | null,
): string | null {
  if (!product?.assets?.length) {
    return null;
  }
  const asset =
    product.assets.find((a) => a.type === "image") ?? product.assets[0];
  return asset?.url ?? null;
}

async function getProductAssociations(
  siteId: string,
  productId: string,
  params: GetProductAssociationsParams,
): Promise<ListProductCardsResponse> {
  const { type, next, limit, count } = params;
  const response = await withJWTClient((client) =>
    client.get<ListProductCardsResponse>(
      `${API_BASE_URL}/${siteId}/products/${productId}/associations/`,
      {
        params: {
          type: type,
          ...(next !== undefined ? { next } : {}),
          ...(limit !== undefined ? { limit } : {}),
          ...(count !== undefined ? { count } : {}),
        },
      },
    ),
  );
  return response.data;
}

export function useGetProductAssociations(
  siteId: string,
  productId: string,
  params: GetProductAssociationsParams,
): UseInfiniteQueryResult<InfiniteData<ListProductCardsResponse>, Error> {
  const normalizedParams = {
    ...params,
    type: params.type,
  } as const;

  return useInfiniteQuery({
    queryKey: [
      ...skuStoreKeys.productAssociations(siteId, productId, normalizedParams),
      "infinite",
    ],
    queryFn: ({ pageParam }: { pageParam: number | undefined }) =>
      getProductAssociations(siteId, productId, {
        ...normalizedParams,
        next: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    initialPageParam: normalizedParams.next,
    enabled: Boolean(siteId) && Boolean(productId),
  });
}

// The backend expects repeated keys: ?metric=views&metric=cart_adds&window=24h&window=7d
function productEngagementSearchParams(
  params?: GetProductEngagementParams,
): URLSearchParams {
  const searchParams = new URLSearchParams();
  params?.metric?.forEach((m) => searchParams.append("metric", m));
  params?.window?.forEach((w) => searchParams.append("window", w));
  return searchParams;
}

async function getProductEngagement(
  siteId: string,
  productId: string,
  params?: GetProductEngagementParams,
): Promise<ProductEngagementResponse> {
  const response = await withJWTClient((client) =>
    client.get<ProductEngagementResponse>(
      `${API_BASE_URL}/${siteId}/products/${productId}/engagement/`,
      { params: productEngagementSearchParams(params) },
    ),
  );
  return response.data;
}

// 404 means "no engagement rows yet": fail fast. Anything else gets two retries.
export function shouldRetryProductEngagement(
  failureCount: number,
  error: unknown,
): boolean {
  return !isNotFoundError(error) && failureCount < 2;
}

export const productEngagementQueryOptions = (
  siteId: string,
  productId: string,
  params?: GetProductEngagementParams,
) =>
  ({
    queryKey: skuStoreKeys.productEngagement(siteId, productId, params),
    queryFn: () => getProductEngagement(siteId, productId, params),
    enabled: Boolean(siteId) && Boolean(productId),
    retry: shouldRetryProductEngagement,
  }) satisfies Pick<
    UseQueryOptions<ProductEngagementResponse, Error>,
    "queryKey" | "queryFn" | "enabled" | "retry"
  >;

/**
 * GET /{site_id}/products/{product_id}/engagement/
 * A 404 means the product has no imported engagement rows yet and is not
 * retried; other failures retry twice before surfacing as an error.
 */
export function useProductEngagement(
  siteId: string,
  productId: string,
  params?: GetProductEngagementParams,
  options?: Omit<
    UseQueryOptions<ProductEngagementResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<ProductEngagementResponse, Error> {
  return useQuery<ProductEngagementResponse, Error>({
    ...productEngagementQueryOptions(siteId, productId, params),
    ...options,
  });
}
